import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bookingActions } from '../../_actions';
import { bookingService } from '../../_services';
import toast from '../shared/Toast';
import { hapticSuccess, hapticError } from '../../_utils/haptics';
import BottomSheet from '../common/BottomSheet/BottomSheet';
import { fmtShort, rememberMoved, salonPhoneOf, rescheduleDeadline, blockedRescheduleCopy, clientStartOf } from './bookingHelpers';

const TimesModal = lazy(() => import('../newSalon/SalonProfilePage/TimesModal'));

/**
 * The client's reschedule and cancel flows, in one place, so the bookings
 * list and the booking details page share exactly the same sheets.
 *
 *   <BookingManageFlows request={{ type: 'reschedule' | 'cancel', booking, nonce }}
 *                       onClose={...} onCancelled={...} history={history} />
 *
 * A new `request` (fresh nonce) starts a flow. Reschedule: preview → time
 * picker (locked to the booked stylist and services) → "Confirm new time"
 * sheet → move → the booking receipt in its "moved" variant. Blocked
 * bookings get a sheet with the reason and a call action. Cancel: fee
 * preview → confirm sheet → cancel.
 */
export default function BookingManageFlows({ request, onClose, onCancelled, history }) {
  const dispatch = useDispatch();
  const booking = request ? request.booking : null;

  // reschedule
  const [preview, setPreview] = useState(null);
  const [timesOpen, setTimesOpen] = useState(false);
  const [pending, setPending] = useState(null); // { date, time, stylist }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [blocked, setBlocked] = useState(null);  // { reason }
  const [moving, setMoving] = useState(false);
  // cancel
  const [cancelOpen, setCancelOpen] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const reset = useCallback(() => {
    setPreview(null); setTimesOpen(false); setPending(null); setConfirmOpen(false); setBlocked(null); setMoving(false);
    setCancelOpen(false); setFeeInfo(null); setFeeLoading(false); setCancelling(false);
  }, []);
  const finish = useCallback(() => { reset(); if (onClose) onClose(); }, [reset, onClose]);

  /* ---------- start a flow when a request arrives ---------- */
  useEffect(() => {
    if (!request || !request.booking) return;
    reset();
    if (request.type === 'reschedule') {
      bookingService.previewReschedule({ bookingId: request.booking._id })
        .then((response) => {
          const d = response?.data?.responseData;
          if (!d || response?.data?.errorCode) {
            toast.error(response?.data?.messages || 'Could not check this booking.');
            finish();
            return;
          }
          if (!d.canReschedule) { setBlocked({ reason: d.reason || '' }); return; }
          setPreview(d);
          setTimesOpen(true);
        })
        .catch(() => { toast.error('Could not check this booking.'); finish(); });
    } else if (request.type === 'cancel') {
      setCancelOpen(true);
      setFeeLoading(true);
      dispatch(bookingActions.previewCancellationFee({ bookingId: request.booking._id }))
        .then((response) => {
          if (response?.data?.statusCode === 1) setFeeInfo(response.data.responseData);
          else toast.error('Failed to load cancellation information');
          setFeeLoading(false);
        })
        .catch(() => { setFeeLoading(false); toast.error('Failed to load cancellation information'); });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request && request.nonce]);

  /* ---------- reschedule pieces ---------- */
  // TimesModal props from the preview: the booking's own services, priced and
  // timed from the booking snapshot, and only the booked stylist offered.
  const services = (preview?.servicesBooked || []).map((sv, i) => ({
    id: String(sv.service || i), name: sv.serviceName, price: sv.servicePrice, duration: sv.serviceTime,
    setupTime: sv.setupTime || 0, cleanupTime: sv.cleanupTime || 0, processingTime: sv.processingTime || 0,
    finishingTime: sv.finishingTime || 0,
  }));
  const hd = (booking && (booking.hairdresserInfo || booking.hairdresser)) || {};
  const stylist = { id: String(preview?.hairdresserId || booking?.hairdresserId || hd._id || ''), name: hd.name || hd.first_name || 'Your stylist' };
  const serviceLabel = (() => {
    const names = services.map((sv) => sv.name).filter(Boolean);
    return names.length > 1 ? `${names[0]} +${names.length - 1} more` : names[0] || 'Your booking';
  })();
  // "Ladies Cut & Finish with Pheobe King, currently Tue 22 Sept, 9:20am. Move it until Mon 21 Sept, 9:20am."
  const subtitle = (() => {
    if (!booking) return null;
    const cur = booking.bookingDate ? clientStartOf(booking) : null;
    const when = cur && !Number.isNaN(cur.getTime()) ? fmtShort(cur) : '';
    const by = rescheduleDeadline(booking, preview);
    return `${serviceLabel}${stylist.name ? ` with ${stylist.name}` : ''}${when ? `, currently ${when}` : ''}.${by ? ` Move it until ${fmtShort(by)}.` : ''}`;
  })();

  const onTimePicked = (details) => { setPending(details); setTimesOpen(false); setConfirmOpen(true); };

  const confirmMove = () => {
    if (!booking || !pending) return;
    const d = pending.date;
    const pad = (n) => String(n).padStart(2, '0');
    const bookingDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const [hh, mm] = String(pending.time).split(':').map(Number);
    setMoving(true);
    bookingService.clientRescheduleBooking({ bookingId: booking._id, bookingDate, startSlot: (hh * 60) + (mm || 0) })
      .then((response) => {
        setMoving(false);
        if (response?.data?.statusCode !== 1) {
          hapticError();
          toast.error(response?.data?.error?.responseMessage || response?.data?.messages || 'Could not reschedule. Please try another time.');
          return;
        }
        hapticSuccess();
        // Hand over to the booking receipt in its "moved" variant: the same
        // page a new booking lands on, so the move feels finished.
        const hid = preview?.hairdresserId || booking.hairdresserId || hd._id || '';
        rememberMoved(booking._id);
        const wasDate = clientStartOf(booking);
        finish();
        if (history && hid) {
          const q = new URLSearchParams({ bid: booking._id, moved: '1', was: Number.isNaN(wasDate.getTime()) ? '' : wasDate.toISOString() });
          history.push(`/book/pro/${hid}/success?${q.toString()}`);
        } else {
          toast.success('Your booking has been moved.');
          if (onCancelled) onCancelled(booking._id, 'moved'); // same effect: the list refreshes
        }
      })
      .catch((err) => { setMoving(false); hapticError(); toast.error(err?.response?.data?.error?.responseMessage || 'Could not reschedule. Please try another time.'); });
  };

  /* ---------- cancel ---------- */
  const confirmCancel = () => {
    if (!booking) return;
    setCancelling(true);
    dispatch(bookingActions.userCancelBooking({ bookingID: booking._id, deviceToken: 'web-client' }))
      .then((response) => {
        setCancelling(false);
        if (response?.data?.statusCode === 1) {
          hapticSuccess();
          toast.success('Booking cancelled successfully');
          finish();
          if (onCancelled) onCancelled(booking._id, 'cancelled');
        } else {
          hapticError();
          toast.error(response?.data?.error?.responseMessage || 'Failed to cancel booking');
        }
      })
      .catch(() => { setCancelling(false); hapticError(); toast.error('Failed to cancel booking. Please try again.'); });
  };

  const phone = salonPhoneOf(booking);
  const call = () => { if (phone) window.location.href = `tel:${phone}`; };
  if (!booking) return null;

  const fmtDay = (d) => d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  const fmt12 = (h, m) => `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`;
  const cur = booking.bookingDate ? clientStartOf(booking) : null;
  const [nh, nm] = String(pending?.time || '').split(':').map(Number);

  return (
    <>
      {/* Reschedule: slot picker (same component as the booking flow) */}
      {timesOpen && preview && (
        <Suspense fallback={null}>
          <TimesModal
            isOpen={timesOpen}
            onClose={finish}
            onBack={finish}
            selectedServices={services}
            team={[stylist]}
            initialStylist={stylist}
            onCheckout={onTimePicked}
            currencySymbol={booking.currencySymbol || '£'}
            salonId={preview.salonId}
            title="Reschedule booking"
            subtitle={subtitle}
            hideBack
            ctaLabel="Continue"
          />
        </Suspense>
      )}

      {/* Reschedule: blocked */}
      <BottomSheet
        open={!!blocked}
        onClose={finish}
        title="Can't move this online"
        footer={
          <>
            <button className="dashboard-btn dashboard-btn-secondary sheet-btn" onClick={finish}>Got it</button>
            {phone && <button className="dashboard-btn dashboard-btn-primary sheet-btn" onClick={call}>Call the salon</button>}
          </>
        }
      >
        {blocked && (
          <>
            <p>{blockedRescheduleCopy(blocked.reason)}</p>
            <p className="dashboard-modal-note">{phone ? 'Call the salon and they can move it for you.' : 'Contact the salon and they can move it for you.'}</p>
          </>
        )}
      </BottomSheet>

      {/* Reschedule: confirm before anything moves */}
      <BottomSheet
        open={confirmOpen}
        onClose={() => { if (!moving) { setConfirmOpen(false); setTimesOpen(true); } }}
        title="Confirm new time"
        footer={
          <>
            <button className="dashboard-btn dashboard-btn-secondary sheet-btn" onClick={() => { setConfirmOpen(false); setTimesOpen(true); }} disabled={moving}>Back</button>
            <button className="dashboard-btn dashboard-btn-primary sheet-btn" onClick={confirmMove} disabled={moving}>{moving ? 'Moving…' : 'Move booking'}</button>
          </>
        }
      >
        {pending && (
          <>
            <p className="dashboard-move-service"><strong>{serviceLabel}</strong>{stylist.name ? ` with ${stylist.name}` : ''}</p>
            <div className="dashboard-move-compare">
              <div className="dashboard-move-row dashboard-move-row--old">
                <span className="dashboard-move-label">Current</span>
                <span className="dashboard-move-when">{cur ? `${fmtDay(cur)}, ${fmt12(cur.getHours(), cur.getMinutes())}` : '--'}</span>
              </div>
              <div className="dashboard-move-arrow" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12l7 7 7-7" /></svg>
              </div>
              <div className="dashboard-move-row dashboard-move-row--new">
                <span className="dashboard-move-label">New</span>
                <span className="dashboard-move-when">{pending.date ? `${fmtDay(pending.date)}, ${Number.isFinite(nh) ? fmt12(nh, nm || 0) : pending.time}` : pending.time}</span>
              </div>
            </div>
            <p className="dashboard-modal-note">Free to move. Same stylist, same price. Any deposit stays attached.</p>
          </>
        )}
      </BottomSheet>

      {/* Cancel */}
      <BottomSheet
        open={cancelOpen}
        onClose={() => { if (!cancelling) finish(); }}
        title="Cancel booking"
        footer={
          <>
            <button className="dashboard-btn dashboard-btn-secondary sheet-btn" onClick={finish} disabled={cancelling}>Keep it</button>
            <button className="dashboard-btn dashboard-btn-danger sheet-btn" onClick={confirmCancel} disabled={cancelling || feeLoading}>{cancelling ? 'Cancelling…' : 'Cancel booking'}</button>
          </>
        }
      >
        <p className="dashboard-move-service"><strong>{serviceLabel === 'Your booking' ? ((booking.servicesBooked || booking.services || [])[0]?.serviceName || 'Your booking') : serviceLabel}</strong>{stylist.name ? ` with ${stylist.name}` : ''}</p>
        <div className="dashboard-move-compare">
          <div className="dashboard-move-row">
            <span className="dashboard-move-label">When</span>
            <span className="dashboard-move-when">{cur && !Number.isNaN(cur.getTime()) ? fmtShort(cur) : '--'}</span>
          </div>
        </div>
        {feeLoading && (
          <div className="dashboard-modal-loading">
            <div className="dashboard-spinner-small"></div>
            <p>Checking the salon's cancellation policy…</p>
          </div>
        )}
        {!feeLoading && feeInfo && (
          <div className="dashboard-modal-fee-info">
            {feeInfo.willBeCharged ? (
              <div className="dashboard-fee-warning">
                <p className="dashboard-fee-label">Cancellation fee</p>
                <p className="dashboard-fee-amount">{feeInfo.currencySymbol}{Number(feeInfo.cancellationFee || 0).toFixed(2)}</p>
                <p className="dashboard-fee-description">Charged to your saved payment method under the salon's cancellation policy.</p>
              </div>
            ) : (
              <div className="dashboard-fee-success"><p>No cancellation fee if you cancel now.</p></div>
            )}
          </div>
        )}
        <p className="dashboard-modal-note">This cannot be undone. Want a different time instead? Close this and choose Reschedule.</p>
      </BottomSheet>
    </>
  );
}
